import React, { useMemo } from 'react';
import { useAITTheme, type AITTheme } from '@this/shared/ui/theme';
import { getCircleNumber } from '@this/src/util/string/circledNumberMarks';
import { formatPriceWithMark } from '@this/src/util/price/price.util';
import {
  PROPOSAL_BUNDLE_CATEGORY,
  TICKET_CATEGORY,
  TICKET_CATEGORY_LABELS,
  type ProposalBundle,
  type TransportProposalItem
} from '../../../types/proposalBundles';
import { TransportTimeRangeCell } from '../../TransportTimeRangeCell';
import { buildForeignFlightRowDisplay } from '../utils';

export type ForeignFlightProposalTransportTableRowProps = {
  item: TransportProposalItem;
  index: number;
  includeTax: boolean;
  onSelect: () => void;
  disabled: boolean;
  hasRoundTripPair: boolean;
  bundleCategory: ProposalBundle['category'];
  compactLayout: boolean;
};

export const ForeignFlightProposalTransportTableRow = ({
  item,
  index,
  includeTax,
  onSelect,
  disabled,
  hasRoundTripPair,
  bundleCategory,
  compactLayout
}: ForeignFlightProposalTransportTableRowProps) => {
  const { theme } = useAITTheme();
  const styles = useMemo(
    () => getForeignFlightTableRowStyles(theme, item.selected ?? false, disabled, compactLayout),
    [theme, item.selected, disabled, compactLayout]
  );
  const companion = useMemo(() => getStyles(theme, compactLayout), [theme, compactLayout]);
  const circleNumber = getCircleNumber(index);
  const rowDisplay = buildForeignFlightRowDisplay(item, hasRoundTripPair);

  const priceFromApi = item.priceText?.trim() ?? '';
  const priceBase = priceFromApi || formatPriceWithMark(includeTax ? item.priceWithTax : item.price);
  const priceDisplay =
    priceBase != null && bundleCategory === PROPOSAL_BUNDLE_CATEGORY.OUTWARD && item.transport.isAir === true
      ? priceFromApi.length > 0
        ? priceBase
        : `${priceBase}（往復）`
      : priceBase ?? '-';

  const handleClick = () => {
    const selectable = (item.selected ?? false) === false && disabled === false;
    if (selectable) {
      onSelect();
    }
  };

  return (
    <tr style={styles.row}>
      <td style={styles.frCellNarrow}>
        <div style={styles.numberCircle}>{circleNumber}</div>
      </td>
      <td style={styles.frCellTime}>
        <div style={styles.frCellTimeInner}>
          <TransportTimeRangeCell
            departureTime={item.transport.departureTime}
            arrivalTime={item.transport.arrivalTime}
            departureDate={item.transport.departureDate}
            arrivalDate={item.transport.arrivalDate}
          />
        </div>
      </td>
      <td style={styles.frCellMuted}>
        <div style={styles.frCellMutedInner}>{rowDisplay.durationMain ?? '—'}</div>
        {rowDisplay.durationSub ? <div style={styles.frCellSubMuted}>{rowDisplay.durationSub}</div> : null}
      </td>
      <td style={styles.frCellSegment}>
        <span style={styles.frCellSegmentInner}>{rowDisplay.segmentLabel}</span>
      </td>
      <td style={styles.frCell}>
        <span style={styles.frCellAirlineCarrier}>
          {rowDisplay.airlineCarrier.length > 0 ? rowDisplay.airlineCarrier : '—'}
        </span>
      </td>
      <td style={styles.frCell}>
        <div>
          <strong>{rowDisplay.flightMain}</strong>
        </div>
        {rowDisplay.ticketExtra ? <div style={companion.ticketSub}>{rowDisplay.ticketExtra}</div> : null}
      </td>
      <td style={styles.frCellSeat}>{rowDisplay.seatLabel}</td>
      <td style={styles.frCellBadge}>
        {item.transport.ticketCategory ? (
          <span
            style={
              item.transport.ticketCategory === TICKET_CATEGORY.CHANGEABLE
                ? styles.badgeChangeable
                : styles.badgeUnchangeable
            }
          >
            {TICKET_CATEGORY_LABELS[item.transport.ticketCategory]}
          </span>
        ) : (
          <span>—</span>
        )}
      </td>
      <td style={{ ...styles.cellPrice, ...companion.frCellPrice }}>
        <div style={companion.priceStack}>
          <span style={companion.priceAmount}>{priceDisplay}</span>
          <button
            type="button"
            style={styles.selectButton}
            onClick={handleClick}
            disabled={item.selected || disabled}
          >
            選択
          </button>
        </div>
      </td>
    </tr>
  );
};

const getStyles = (theme: AITTheme, compactHeader: boolean) => ({
  frCellPrice: {
    padding: compactHeader ? '6px 2px 6px 0' : '6px 4px 6px 2px',
    textAlign: 'right' as const,
    verticalAlign: 'middle' as const,
    minWidth: 0
  },
  priceAmount: compactHeader
    ? {
        whiteSpace: 'nowrap' as const,
        fontWeight: 700 as const,
        color: theme.linkColor,
        fontSize: '14px',
        lineHeight: 1.35,
        flex: '0 0 auto',
        textAlign: 'right' as const
      }
    : {
        whiteSpace: 'nowrap' as const,
        fontWeight: 700 as const,
        color: theme.linkColor,
        fontSize: '14px',
        lineHeight: 1.35,
        flex: '1 1 auto',
        minWidth: 0,
        textAlign: 'right' as const
      },
  priceStack: {
    display: 'flex',
    flexDirection: 'row' as const,
    flexWrap: compactHeader ? ('nowrap' as const) : ('wrap' as const),
    alignItems: 'center' as const,
    justifyContent: 'flex-end' as const,
    alignContent: 'flex-end',
    gap: compactHeader ? '4px' : '6px',
    width: '100%',
    minWidth: 0,
    boxSizing: 'border-box' as const
  },
  ticketSub: {
    marginTop: '4px',
    fontSize: '10px',
    color: '#666666',
    fontWeight: 500 as const,
    lineHeight: 1.35
  }
});

const getForeignFlightTableRowStyles = (
  theme: AITTheme,
  selected: boolean,
  disabled: boolean,
  compactLayout = false
) => ({
  row: {
    borderBottom: '1px solid #ececec',
    backgroundColor: selected ? '#e6f4fb' : 'transparent'
  },
  numberCircle: {
    width: '24px',
    height: '24px',
    minWidth: '24px',
    backgroundColor: theme.linkColor,
    color: '#ffffff',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700 as const,
    fontSize: '10px'
  },
  badgeChangeable: {
    display: 'inline-block',
    padding: '2px 6px',
    borderRadius: '4px',
    backgroundColor: '#e6f4fb',
    color: theme.linkColor,
    fontWeight: 600 as const,
    fontSize: '10px',
    whiteSpace: 'nowrap' as const,
    boxSizing: 'border-box' as const
  },
  badgeUnchangeable: {
    display: 'inline-block',
    padding: '2px 6px',
    borderRadius: '4px',
    backgroundColor: '#fff4e5',
    color: '#b45309',
    fontWeight: 600 as const,
    fontSize: '10px',
    whiteSpace: 'nowrap' as const,
    boxSizing: 'border-box' as const
  },
  cellPrice: {
    padding: '6px 4px',
    textAlign: 'right' as const,
    verticalAlign: 'middle' as const
  },
  selectButton: {
    backgroundColor: theme.linkColor,
    color: '#ffffff',
    border: 'none',
    padding: '6px 10px',
    borderRadius: '4px',
    fontSize: '11px',
    fontWeight: 600 as const,
    cursor: selected || disabled ? 'not-allowed' : 'pointer',
    whiteSpace: 'nowrap' as const,
    opacity: selected || disabled ? 0.5 : 1,
    flexShrink: 0
  },
  frCellNarrow: {
    padding: '5px 2px',
    verticalAlign: 'middle' as const,
    width: '32px',
    minWidth: 0
  },
  frCellTime: {
    padding: compactLayout ? '6px 4px' : '10px 6px',
    fontSize: compactLayout ? '11px' : '12px',
    fontWeight: 600 as const,
    verticalAlign: 'top' as const,
    minWidth: 0,
    maxWidth: '100%',
    overflow: 'visible' as const
  },
  frCellTimeInner: {
    display: 'block' as const,
    whiteSpace: 'normal' as const,
    wordBreak: 'break-word' as const,
    overflowWrap: 'anywhere' as const,
    lineHeight: 1.45
  },
  frCellMuted: {
    padding: compactLayout ? '6px 4px' : '10px 6px',
    fontSize: compactLayout ? '10px' : '12px',
    color: '#555555',
    verticalAlign: 'top' as const,
    lineHeight: 1.45,
    minWidth: 0,
    maxWidth: '100%',
    overflow: 'visible' as const
  },
  frCellMutedInner: {
    display: 'block' as const,
    wordBreak: 'break-word' as const,
    overflowWrap: 'anywhere' as const,
    lineHeight: 1.45
  },
  frCellSubMuted: {
    marginTop: '2px',
    fontSize: '10px',
    color: '#777777',
    fontWeight: 500 as const
  },
  frCellSegment: {
    padding: compactLayout ? '6px 2px' : '6px 4px',
    fontSize: '10px',
    verticalAlign: 'top' as const,
    lineHeight: 1.45,
    color: '#333333',
    minWidth: 0,
    maxWidth: '100%',
    overflow: 'hidden' as const
  },
  frCellSegmentInner: {
    display: 'block' as const,
    wordBreak: 'break-word' as const,
    overflowWrap: 'anywhere' as const,
    overflow: 'hidden' as const
  },
  frCell: {
    padding: compactLayout ? '6px 2px' : '6px 4px',
    fontSize: '11px',
    verticalAlign: 'top' as const,
    lineHeight: 1.45,
    minWidth: 0,
    maxWidth: '100%',
    overflow: 'hidden' as const,
    wordBreak: 'break-word' as const,
    overflowWrap: 'anywhere' as const
  },
  frCellAirlineLine: {
    fontSize: '10px',
    color: '#333333',
    marginBottom: '2px',
    overflow: 'hidden' as const,
    textOverflow: 'ellipsis' as const,
    whiteSpace: 'nowrap' as const
  },
  frCellAirlineCarrier: {
    display: 'block' as const,
    fontSize: '10px',
    color: '#333333',
    overflow: 'hidden' as const,
    textOverflow: 'ellipsis' as const,
    whiteSpace: 'nowrap' as const
  },
  frCellSeat: {
    padding: compactLayout ? '4px 2px' : '6px 4px',
    fontSize: '10px',
    color: '#333333',
    verticalAlign: 'middle' as const,
    lineHeight: 1.45,
    minWidth: 0,
    maxWidth: '100%',
    overflow: 'hidden' as const,
    textOverflow: 'ellipsis' as const,
    whiteSpace: 'nowrap' as const
  },
  frCellBadge: {
    padding: compactLayout ? '4px 2px' : '6px 2px 6px 4px',
    textAlign: 'center' as const,
    verticalAlign: 'middle' as const,
    fontSize: compactLayout ? '10px' : '11px',
    minWidth: 0,
    boxSizing: 'border-box' as const
  }
});
